import { err, ok, type Result } from "./result";

export type KnownIdOption<Identifier extends string> = {
  readonly _id: Identifier;
};

export type KnownIdInputError =
  | { readonly kind: "missingKnownId" }
  | { readonly kind: "unknownKnownId"; readonly value: string };

export const cleanIdInput = (value: string | null): string => value?.trim() ?? "";

export const knownOptionInputResult = <Option extends KnownIdOption<string>>(
  options: readonly Option[],
  value: string | null,
): Result<Option, KnownIdInputError> => {
  const cleaned = cleanIdInput(value);
  if (cleaned === "") return err({ kind: "missingKnownId" });

  const known = options.find((option) => option._id === cleaned);
  return known === undefined
    ? err({ kind: "unknownKnownId", value: cleaned })
    : ok(known);
};

export const knownIdInputResult = <Identifier extends string>(
  options: readonly KnownIdOption<Identifier>[],
  value: string | null,
): Result<Identifier, KnownIdInputError> => {
  const result = knownOptionInputResult(options, value);
  return result.ok ? ok(result.value._id) : err(result.error);
};

export const knownIdFromInput = <
  Identifier extends string,
  Fallback extends Identifier | null | "",
>(
  options: readonly KnownIdOption<Identifier>[],
  value: string | null,
  fallback: Fallback,
): Identifier | Fallback => {
  const result = knownIdInputResult(options, value);
  return result.ok ? result.value : fallback;
};

export const knownIdOrEmptyFromInput = <Identifier extends string>(
  options: readonly KnownIdOption<Identifier>[],
  value: string,
  fallback: Identifier | "",
): Identifier | "" => {
  const result = knownIdInputResult(options, value);
  if (result.ok) return result.value;
  return result.error.kind === "missingKnownId" ? "" : fallback;
};

export const knownIdOrNullFromInput = <Identifier extends string>(
  options: readonly KnownIdOption<Identifier>[],
  value: string,
  fallback: Identifier | null,
): Identifier | null => {
  const result = knownIdInputResult(options, value);
  if (result.ok) return result.value;
  return result.error.kind === "missingKnownId" ? null : fallback;
};
